import { Component } from '../component.interface';
import { CookieManagementCookies } from './model/cookies.model';
import { ServiceInjectorService } from './service/service-injector.service';
import { CookieManagementConfig } from './config/cookie-management-config.interface';
import { cookieManagementDefaultConfig } from './config/cookie-management-default.config';
import { CookiesEnum } from './model/cookies.enum';
import { CookieManagementDomInjector } from './cookie-management-dom-injector';
import { CookiesHooksEnum } from './model/cookies-hooks.enum';
import { pairwise } from 'rxjs';

export class CookieManagement implements Component {
	bootstrapped = false;
	private readonly config = cookieManagementDefaultConfig;
	private cookies: CookieManagementCookies = new CookieManagementCookies();
	private cookieManagementDomInjector: CookieManagementDomInjector = null;
	private cookieManagementServiceInjector: ServiceInjectorService = null;

	constructor(private window: Window, private document: Document, config: CookieManagementConfig = null) {
		this.config = { ...this.config, ...config };
		this.cookieManagementServiceInjector = new ServiceInjectorService(this.window, this.document);
	}

	private readConsent(): boolean {
		const cookieValueRegExpString =
			'(all=(?:true|false)(?:&[a-zA-Z]+(?:=[a-zA-Z]+)?)*&v=' +
			CookieManagementCookies.COOKIE_VERSION +
			'\\.' +
			this.config.majorVersion +
			'\\.(?:[1-9][0-9]*|[0-9]))(?:.*)?$';

		this.setCookieValueFromUrl(cookieValueRegExpString);

		const query = new RegExp('^(?:.*;)?\\s*' + this.config.cookieName + '\\s*=\\s*' + cookieValueRegExpString);

		let cookieValue = (this.document.cookie.match(query) || [undefined, null])[1];
		if (cookieValue === null) {
			return this.cookieManagementServiceInjector.commitHook(false, CookiesHooksEnum.read);
		}

		this.cookies.deserialize(cookieValue);
		return this.cookieManagementServiceInjector.commitHook(true, CookiesHooksEnum.read);
	}

	getResource(key: string): string {
		if (this.config.resources.hasOwnProperty(key)) {
			return this.config.resources[key];
		}

		return key;
	}

	private writeConsent(): void {
		const cookieDate = new Date();
		cookieDate.setDate(cookieDate.getDate() + this.config.cookieExpirationDays);

		const cookieDomain = this.config.cookieDomain === null ? this.window.location.host : this.config.cookieDomain;

		let cookieValue = `${this.config.cookieName}=${this.cookies.serialize(
			this.config.majorVersion,
			this.config.minorVersion
		)};`;
		cookieValue += `expires=${cookieDate.toUTCString()};`;
		cookieValue += `domain=${cookieDomain};`;
		cookieValue += `path=${this.config.cookiePath};`;
		cookieValue += `SameSite=${this.config.cookieSameSite}`;
		if (this.config.cookieSecure) {
			cookieValue += '; secure';
		}

        this.document.cookie = cookieValue;
		this.dispatchConsentCookieCreatedCustomEvent();
    }

	private dispatchConsentCookieCreatedCustomEvent(): void {
		this.window?.dispatchEvent(new CustomEvent('consentCookieCreated'));
	}

	private setCookieValueFromUrl(cookieValueRegExpString: string): void {
		const cookieQueryParam = this.getCookieQueryParam();

		if (cookieQueryParam == null) {
			return;
		}

		const decodedCookieQueryParam = atob(cookieQueryParam);

		const cookieValue = (decodedCookieQueryParam.match(new RegExp(cookieValueRegExpString)) || [undefined, null])[1];

		if (cookieValue !== null) {
			this.cookies.setEachForEach(this.getCookieMapFromUrl(cookieValue));
			this.writeConsent();
		}
	}

	private getCookieMapFromUrl(cookieValue: string): Map<CookiesEnum, boolean> {
		const cookieSearchParam = new URLSearchParams(cookieValue);

		const cookiesMap = new Map<CookiesEnum, boolean>();
		this.updateCookieMapForTrueOrFalseCookieValues(cookiesMap, cookieSearchParam, CookiesEnum.all);
		this.updateCookieMapForTrueOrFalseCookieValues(cookiesMap, cookieSearchParam, CookiesEnum.analytics);
		this.updateCookieMapForTrueOrFalseCookieValues(cookiesMap, cookieSearchParam, CookiesEnum.marketing);

		return cookiesMap;
	}

	updateCookieMapForTrueOrFalseCookieValues(
		cookiesMap: Map<CookiesEnum, boolean>,
		cookieSearchParam: URLSearchParams,
		cookieEnum: CookiesEnum
	): void {
		const cookieValueFromUrl = cookieSearchParam.get(cookieEnum);

		if (cookieValueFromUrl && (cookieValueFromUrl === 'true' || cookieValueFromUrl === 'false')) {
			cookiesMap.set(cookieEnum, cookieValueFromUrl === 'true');
		}
	}

	private getCookieQueryParam(): string {
		const queryString = this.window.location.search;
		const urlParams = new URLSearchParams(queryString);
		return urlParams.get(this.config.cookieName);
	}

	init(): void {
		this.cookies.cookiesSubjectMap.forEach((source, $key) =>
			source.pipe(pairwise()).subscribe(([previousData, data]: [boolean, boolean]) => {
				if (!data && previousData) {
					this.cookieManagementServiceInjector.reject();
					return;
				}

				if (data) {
					this.cookieManagementServiceInjector.resolve($key);
				}
			})
		);

		this.cookieManagementDomInjector.registerControls(this.cookies);

		if (
			this.readConsent() ||
			!this.config.showConsent ||
			this.window.location.href.match(this.config.excludeFrom) !== null
		) {
			return;
		}

		this.cookieManagementDomInjector.openIntroWindow();
	}

	acceptAll(): void {
		this.cookies.setOneForEach(true);
		this.writeConsent();
	}

	denyAll(): void {
		const cookiesMap = new Map<CookiesEnum, boolean>();
		cookiesMap.set(CookiesEnum.all, false);
		cookiesMap.set(CookiesEnum.analytics, false);
		cookiesMap.set(CookiesEnum.marketing, false);
		this.cookies.setEachForEach(cookiesMap);
		this.writeConsent();
	}

	acceptMap(cookiesBooleanMap: Map<CookiesEnum, boolean>): void {
		cookiesBooleanMap.set(CookiesEnum.all, false);
		this.cookies.setEachForEach(cookiesBooleanMap);
		this.writeConsent();
	}

	injectDom(hostElement?: HTMLElement): void {
		this.cookieManagementDomInjector = new CookieManagementDomInjector(this.document, hostElement, this);
	}

	resolveAfterConsent(serviceFn: () => void, cookieClass: CookiesEnum): void {
		this.cookieManagementServiceInjector.prepare(serviceFn, cookieClass);
	}

	resolveAfterHook(serviceFn: (state: boolean) => void, cookieHook: CookiesHooksEnum): void {
		this.cookieManagementServiceInjector.hook(serviceFn, cookieHook);
	}

	editCookies(): void {
		this.cookieManagementDomInjector.openEditWindow();
	}
}
